/**
 * ChatMessage - Componente individual de mensagem
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { ConversationMessageResponse } from '@troia-v3/types';
import MessageStatus, { MessageStatusType } from './MessageStatus';
import { SendTemplateModal } from './SendTemplateModal';
import { DocumentPreview } from './DocumentPreview';
import { LocationPreview } from './LocationPreview';
import { ContactCard } from './ContactCard';
import AudioPlayer from './AudioPlayer';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import MessageReplyPreview from './MessageReplyPreview';
import { useReplyMessage } from '../hooks/useReplyMessage';
import { pauseOtherMedia } from '../utils/mediaPlayback';
import { TranscribeButton } from './TranscribeButton';
import { useIsMobile } from '@/shared/hooks';
import { Icon } from '@/shared/icons'
import { Tooltip } from '@/shared/components/ui';
import { META_WINDOW_CODES } from '../hooks/useMessagingWindow';
export type MessageAction = 'edit' | 'delete' | 'reply' | 'forward' | 'react';

interface ChatMessageProps {
  message: ConversationMessageResponse;
  isOwn: boolean;
  showAvatar?: boolean;
  onAction?: (action: MessageAction, message: ConversationMessageResponse) => void;
  onReact?: (messageId: string, emoji: string) => void;
  onRemoveReaction?: (messageId: string, emoji: string) => void;
  onImageClick?: (messageId: string) => void;
  isHighlighted?: boolean;
  currentUserId?: string;
  /** ID do canal da conversa — necessário para abrir o modal de template */
  channelId?: string;
  /** Se o canal suporta envio de templates (ProviderCapability.TEMPLATE_MANAGEMENT) */
  channelSupportsTemplates?: boolean;
  /** Canal permite editar mensagem enviada (falso em WhatsApp oficial/Meta). */
  channelSupportsEdit?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  isOwn,
  showAvatar = true,
  onAction,
  onReact,
  onRemoveReaction,
  onImageClick,
  isHighlighted = false,
  currentUserId,
  channelId,
  channelSupportsTemplates,
  channelSupportsEdit = true,
}) => {
  const [showActions, setShowActions] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [isSystemMessageExpanded, setIsSystemMessageExpanded] = useState(false);
  const [openReactionEmoji, setOpenReactionEmoji] = useState<string | null>(null);
  const [reactionTab, setReactionTab] = useState<string>('all');
  const [reactionDropdownPos, setReactionDropdownPos] = useState<{ bottom: number; left?: number; right?: number }>({ bottom: 0 });
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [showEmojiBar, setShowEmojiBar] = useState(false);
  // Mobile (#087): tap na mensagem abre bottom action sheet (reações + ações),
  // em vez dos triggers hover-only + portais posicionados p/ desktop.
  const isMobile = useIsMobile();
  const [showMobileSheet, setShowMobileSheet] = useState(false);

  // Light dismiss (DS §Overlays): action sheet fecha por scrim ou Escape
  useEffect(() => {
    if (!showMobileSheet) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowMobileSheet(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showMobileSheet]);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [emojiBarPos, setEmojiBarPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // WhatsApp permite editar mensagens apenas dentro de 15min após envio.
  // Só mensagens outbound com providerMessageId e dentro da janela são editáveis.
  const EDIT_WINDOW_MS = 15 * 60 * 1000;
  const sentAt = message.sentAt || message.createdAt;
  const canEdit = Boolean(
    isOwn
    && channelSupportsEdit
    && message.providerMessageId
    && sentAt
    && (Date.now() - new Date(sentAt).getTime()) <= EDIT_WINDOW_MS
    && message.content.some(c => c.type === 'text')
  );

  const reactionDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const emojiBarRef = useRef<HTMLDivElement>(null);
  const chevronBtnRef = useRef<HTMLButtonElement>(null);
  const emojiBtnRef = useRef<HTMLButtonElement>(null);

  const openDropdown = useCallback(() => {
    if (chevronBtnRef.current) {
      const rect = chevronBtnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.right - 175 });
    }
    setShowDropdownMenu(true);
    setShowEmojiBar(false);
  }, []);

  const openEmojiBar = useCallback(() => {
    if (emojiBtnRef.current) {
      const rect = emojiBtnRef.current.getBoundingClientRect();
      if (isOwn) {
        setEmojiBarPos({ top: rect.top - 46, left: rect.right - 250 });
      } else {
        setEmojiBarPos({ top: rect.top - 46, left: rect.left });
      }
    }
    setShowEmojiBar(true);
    setShowDropdownMenu(false);
  }, [isOwn]);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const anyOpen = openReactionEmoji || showDropdownMenu || showEmojiBar;
    if (!anyOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (openReactionEmoji && reactionDropdownRef.current && !reactionDropdownRef.current.contains(target)) {
        setOpenReactionEmoji(null);
      }
      if (showDropdownMenu && dropdownMenuRef.current && !dropdownMenuRef.current.contains(target)) {
        setShowDropdownMenu(false);
        setShowActions(false);
      }
      if (showEmojiBar && emojiBarRef.current && !emojiBarRef.current.contains(target)) {
        setShowEmojiBar(false);
        setShowActions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openReactionEmoji, showDropdownMenu, showEmojiBar]);

  // ✅ PERFORMANCE OPTIMIZATION: Fetch reply on-demand (não vem mais do backend)
  const { data: replyData } = useReplyMessage(message.replyToMessageId);

  // Usar reply do backend (legacy) ou do hook (novo)
  const reply = message.reply || replyData;

  // Check if message is system type
  const isSystemMessage = message.messageType === 'system';

  // Emojis que o usuário atual já reagiu nesta mensagem
  const myReactedEmojis = new Set(
    (message.reactions || [])
      .filter(r => r.userId && r.userId === currentUserId)
      .map(r => r.emoji)
  );

  // Check if message is from AI (verificar senderType ao invés de messageType)
  const isAIMessage = message.senderType === 'ai';

  // Check if message is from workflow automation
  const isAutomationMessage = message.senderType === 'automation';

  const formatVideoDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  type QuickReplyButtonItem = {
    id?: string;
    text: string;
    onClick?: () => void;
    disabled?: boolean;
  };

  const renderQuickReplyButtons = (buttons: QuickReplyButtonItem[]) => (
    <div
      className="flex flex-col mt-2 -mx-4 -mb-1"
      style={{ borderTop: '1px solid color-mix(in srgb, currentColor 10%, transparent)' }}
    >
      {buttons.map((btn, idx) => {
        const clickable = !btn.disabled && typeof btn.onClick === 'function';
        return (
          <button
            key={btn.id ?? `btn-${idx}`}
            type="button"
            disabled={!clickable}
            onClick={clickable ? btn.onClick : undefined}
            className="px-2.5 py-2 text-[13px] font-semibold text-center disabled:cursor-default"
            style={{
              background: 'transparent',
              color: isOwn ? 'rgba(255,255,255,0.95)' : 'var(--theme-primary, #6366f1)',
              border: 'none',
              borderTop: idx === 0 ? 'none' : '1px solid color-mix(in srgb, currentColor 10%, transparent)',
              cursor: clickable ? 'pointer' : 'default',
            }}
            data-testid={`chat-message-button-${idx}`}
          >
            {btn.text}
          </button>
        );
      })}
    </div>
  );

  const renderMediaPreview = () => {
    // Transcrição de áudio/vídeo (STT) — o backend grava em plainText. Sem STT, o
    // plainText de vídeo repete o caption (extractPlainText) e o de áudio fica vazio.
    // Mensagens com item 'text' já exibem o texto via getMessageContent — não duplicar.
    const hasTextContent = message.content.some(c => c.type === 'text');
    const transcription = !hasTextContent && message.plainText ? message.plainText.trim() : '';

    // Image content
    const imageContent = message.content.find(c => c.type === 'image');
    if (imageContent && 'url' in imageContent && imageContent.url) {
      const caption = 'caption' in imageContent && imageContent.caption ? String(imageContent.caption) : '';
      return (
        <>
          <div className="p-[3px] -mx-4 -my-2 mb-0 rounded-[10px] overflow-hidden">
            <img
              src={imageContent.url}
              alt={caption || 'Imagem'}
              className="w-full max-w-[320px] h-[200px] object-cover block cursor-pointer hover:opacity-90 transition-opacity"
              style={{ borderRadius: 'calc(var(--chat-bubble-radius) - 6px)' }}
              onClick={() => onImageClick?.(message.id)}
              data-testid="chat-message-image"
            />
          </div>
          {caption && (
            <p className="px-[10px] pt-1.5 pb-0 text-[13.5px] mb-0 whitespace-pre-wrap break-words">
              {caption}
            </p>
          )}
        </>
      );
    }

    // Video content
    const videoContent = message.content.find(c => c.type === 'video');
    if (videoContent && 'url' in videoContent && videoContent.url) {
      const caption = 'caption' in videoContent && videoContent.caption ? String(videoContent.caption) : '';
      const duration = 'duration' in videoContent && typeof videoContent.duration === 'number' ? videoContent.duration : undefined;
      return (
        <>
          <div className="p-[3px] -mx-4 -my-2 mb-0 rounded-[10px] overflow-hidden relative">
            <video
              src={videoContent.url}
              controls
              onPlay={(e) => pauseOtherMedia(e.currentTarget)}
              className="w-full max-w-[320px] h-[200px] object-cover block"
              style={{ borderRadius: 'calc(var(--chat-bubble-radius) - 6px)' }}
              data-testid="chat-message-video"
            />
            {duration !== undefined && (
              <span
                className="absolute bottom-2 right-2 px-1.5 py-0.5 text-[11px] font-medium rounded tabular-nums"
                style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}
              >
                {formatVideoDuration(duration)}
              </span>
            )}
          </div>
          {caption && (
            <p className="px-[10px] pt-1.5 pb-0 text-[13.5px] mb-0 whitespace-pre-wrap break-words">
              {caption}
            </p>
          )}
          {transcription && transcription !== caption.trim() && (
            <p
              className="px-[10px] pt-1.5 pb-0 text-[13.5px] mb-0 whitespace-pre-wrap break-words"
              data-testid="chat-message-transcription"
            >
              {transcription}
            </p>
          )}
        </>
      );
    }

    // Audio content
    const audioContent = message.content.find(c => c.type === 'audio');
    if (audioContent && 'url' in audioContent && audioContent.url) {
      return (
        <>
          <AudioPlayer src={audioContent.url} isOwn={isOwn} />
          {transcription && (
            <div
              className="flex items-start gap-1.5 mx-[10px] mt-1.5 pt-1.5 text-[13.5px] whitespace-pre-wrap break-words"
              style={{
                borderTop: `1px dashed ${isOwn ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.12)'}`,
                opacity: 0.85,
              }}
              data-testid="chat-message-transcription"
            >
              <Icon name="cpu" className="w-3.5 h-3.5 flex-none mt-0.5" />
              <span>{transcription}</span>
            </div>
          )}
        </>
      );
    }

    // Document content - NEW
    const documentContent = message.content.find(c => c.type === 'document');
    if (documentContent && 'url' in documentContent && documentContent.url && 'filename' in documentContent) {
      return <DocumentPreview content={documentContent as any} isOwn={isOwn} />;
    }

    // Location content - NEW
    const locationContent = message.content.find(c => c.type === 'location');
    if (locationContent && 'latitude' in locationContent && 'longitude' in locationContent) {
      return <LocationPreview content={locationContent as any} />;
    }

    // Contact content - NEW
    const contactContent = message.content.find(c => c.type === 'contact');
    if (contactContent && 'name' in contactContent) {
      return <ContactCard content={contactContent as any} isOwn={isOwn} />;
    }

    // Link preview content - P5 Glass
    const linkContent = message.content.find(c => c.type === 'link');
    if (linkContent && 'url' in linkContent && linkContent.url) {
      const lc = linkContent as { type: 'link'; url: string; title?: string; description?: string; image?: string };
      let hostname = '';
      try {
        hostname = new URL(lc.url).hostname;
      } catch {
        hostname = lc.url;
      }
      return (
        <div
          className="rounded-md overflow-hidden mb-1 cursor-pointer"
          style={{
            borderLeft: `3px solid ${isOwn ? 'var(--chat-bubble-reply-accent-out)' : 'var(--chat-bubble-reply-accent-in)'}`,
            background: isOwn ? 'var(--chat-bubble-reply-bg-out)' : 'var(--chat-bubble-reply-bg-in)',
          }}
          onClick={() => window.open(lc.url, '_blank', 'noopener,noreferrer')}
          data-testid="chat-message-link"
        >
          {lc.image && (
            <div
              className="w-full h-[110px]"
              style={{ background: `url(${lc.image}) center/cover` }}
            />
          )}
          <div className="px-2.5 py-2">
            <p
              className="text-[10.5px] font-semibold uppercase tracking-wider m-0 mb-0.5 break-words"
              style={{ color: isOwn ? 'rgba(255,255,255,0.9)' : 'var(--chat-bubble-reply-accent-in)' }}
            >
              {hostname}
            </p>
            {lc.title && (
              <p className="text-[13px] font-semibold m-0 mb-0.5 leading-tight line-clamp-2">
                {lc.title}
              </p>
            )}
            {lc.description && (
              <p className="text-[11.5px] opacity-70 m-0 leading-snug line-clamp-2">
                {lc.description}
              </p>
            )}
          </div>
        </div>
      );
    }

    // Poll content (enquete recebida) — a plataforma passou a receber enquetes
    // em 2026-08-24; antes eram descartadas no backend.
    const pollContent = message.content.find((c: any) => c.type === 'poll');
    if (pollContent) {
      const pc = pollContent as unknown as { type: 'poll'; name?: string; options?: string[]; selectableCount?: number };
      const pollOptions = pc.options ?? [];
      return (
        <div className="flex flex-col gap-2 text-[13px]" data-testid="chat-message-poll">
          <div className="flex items-center gap-2">
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'color-mix(in srgb, currentColor 12%, transparent)' }}
            >
              <Icon name="bar-chart" className="w-3.5 h-3.5" />
            </span>
            <p className="font-medium m-0 break-words min-w-0">{pc.name || 'Enquete'}</p>
          </div>
          {pollOptions.length > 0 && (
            <ul className="m-0 pl-0 list-none flex flex-col gap-1">
              {pollOptions.map((opt, i) => (
                <li
                  key={`${opt}-${i}`}
                  className="px-2.5 py-1.5 rounded-lg break-words"
                  style={{ background: 'color-mix(in srgb, currentColor 8%, transparent)' }}
                  data-testid={`chat-message-poll-option-${i}`}
                >
                  {opt}
                </li>
              ))}
            </ul>
          )}
          {(pc.selectableCount ?? 0) > 1 && (
            <p className="text-[11px] opacity-65 m-0">Pode escolher até {pc.selectableCount} opções</p>
          )}
        </div>
      );
    }

    // Poll vote content - P5 Glass
    const pollVoteContent = message.content.find((c: any) => c.type === 'poll-vote');
    if (pollVoteContent) {
      const pv = pollVoteContent as unknown as {
        type: 'poll-vote';
        options?: string[];
        pollTitle?: string;
        isRetraction?: boolean;
      };
      // `options` vem resolvido do hash pelo backend. Vazio = enquete original
      // não encontrada (voto sem tradução) ou voto retirado.
      const votedOptions = pv.options ?? [];
      const voteLabel = pv.isRetraction
        ? (isOwn ? 'Você removeu seu voto' : 'Removeu o voto')
        : votedOptions.length > 0
          ? (isOwn ? 'Você votou em' : 'Votou em')
          : (isOwn ? 'Você votou na enquete' : 'Votou na enquete');
      return (
        <div className="flex items-center gap-2 text-[13px]" data-testid="chat-message-poll-vote">
          <span
            className="w-7 h-7 rounded-full flex items-center justify-center"
            style={{ background: 'color-mix(in srgb, currentColor 12%, transparent)' }}
          >
            <Icon name="bar-chart" className="w-3.5 h-3.5" />
          </span>
          <div className="min-w-0">
            <p className="font-medium m-0 break-words">
              {voteLabel}
              {votedOptions.length > 0 && (
                <>
                  {' '}
                  <span
                    className="font-bold"
                    style={{
                      color: isOwn ? '#fff' : 'var(--chat-bubble-reply-accent-in)',
                      textDecoration: isOwn ? 'underline' : undefined,
                      textUnderlineOffset: 2,
                      textDecorationThickness: 2,
                    }}
                  >
                    {votedOptions.join(', ')}
                  </span>
                </>
              )}
            </p>
            {pv.pollTitle && (
              <p className="text-[11px] opacity-65 m-0 mt-0.5">Enquete: {pv.pollTitle}</p>
            )}
          </div>
        </div>
      );
    }

    // Album content - P5 Glass
    const albumContent = message.content.find((c: any) => c.type === 'album');
    if (albumContent) {
      const ac = albumContent as unknown as {
        type: 'album';
        images?: { url: string }[];
        caption?: string;
      };
      const images = ac.images || [];
      return (
        <>
          <div
            className="grid grid-cols-2 gap-[3px] rounded-[7px] overflow-hidden"
            style={{ gridTemplateRows: '160px 160px' }}
            data-testid="chat-message-album"
          >
            {images.slice(0, 4).map((img, idx) => (
              <div key={idx} className="relative overflow-hidden">
                <img src={img.url} alt="" className="w-full h-full object-cover" />
                {idx === 3 && images.length > 4 && (
                  <div
                    className="absolute inset-0 flex items-center justify-center text-white text-[28px] font-bold backdrop-blur-sm"
                    style={{ background: 'rgba(0,0,0,0.55)' }}
                  >
                    +{images.length - 4}
                  </div>
                )}
              </div>
            ))}
          </div>
          {ac.caption && (
            <p className="px-2.5 pt-1.5 text-[13px] m-0">{ac.caption}</p>
          )}
        </>
      );
    }

    // Buttons content (interactive - view-only)
    const buttonsContent = message.content.find((c: any) => c.type === 'buttons');
    if (buttonsContent && 'buttons' in buttonsContent) {
      const bc = buttonsContent as any;
      const items: QuickReplyButtonItem[] = (bc.buttons || []).map((btn: any) => ({
        id: btn.id,
        text: btn.text || btn.id,
      }));
      return (
        <div>
          {bc.text && <p className="text-sm whitespace-pre-wrap break-words">{bc.text}</p>}
          {bc.footer && (
            <p className="text-xs mt-1 break-words" style={{ color: isOwn ? 'rgba(255,255,255,0.7)' : 'var(--theme-muted-text)' }}>
              {bc.footer}
            </p>
          )}
          {renderQuickReplyButtons(items)}
        </div>
      );
    }

    // List content (interactive - view-only)
    const listContent = message.content.find((c: any) => c.type === 'list');
    if (listContent && 'sections' in listContent) {
      const lc = listContent as any;
      const items: QuickReplyButtonItem[] = [{ text: lc.buttonText || 'Menu' }];
      return (
        <div>
          {lc.text && <p className="text-sm whitespace-pre-wrap break-words">{lc.text}</p>}
          {lc.footer && (
            <p className="text-xs mt-1 break-words" style={{ color: isOwn ? 'rgba(255,255,255,0.7)' : 'var(--theme-muted-text)' }}>
              {lc.footer}
            </p>
          )}
          {(lc.sections || []).map((section: any, si: number) => (
            <div key={si} className="mt-2">
              {section.title && (
                <p className="text-xs font-semibold mb-1" style={{ color: isOwn ? 'rgba(255,255,255,0.85)' : 'var(--theme-muted-text)' }}>
                  {section.title}
                </p>
              )}
              {(section.rows || []).map((row: any, ri: number) => (
                <div
                  key={ri}
                  className="py-1 text-sm"
                  style={{ color: isOwn ? 'rgba(255,255,255,0.95)' : 'var(--theme-card-text)' }}
                >
                  <span className="font-medium">{row.title}</span>
                  {row.description && (
                    <span
                      className="text-xs ml-1"
                      style={{ color: isOwn ? 'rgba(255,255,255,0.7)' : 'var(--theme-muted-text)' }}
                    >
                      {' '}- {row.description}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
          {renderQuickReplyButtons(items)}
        </div>
      );
    }

    // Template content (interactive - view-only for replies; URL buttons stay clickable)
    const templateContent = message.content.find((c: any) => c.type === 'template');
    if (templateContent && 'buttons' in templateContent) {
      const tc = templateContent as any;
      const items: QuickReplyButtonItem[] = (tc.buttons || []).map((btn: any) => {
        const isUrl = btn.type === 'url' && btn.url;
        const suffix = btn.type === 'url'
          ? ' \u2197'
          : (btn.type === 'call' && btn.phoneNumber ? ' \u260E' : '');
        return {
          id: btn.id,
          text: `${btn.text}${suffix}`,
          ...(isUrl
            ? {
                disabled: false,
                onClick: () => window.open(btn.url, '_blank', 'noopener,noreferrer'),
              }
            : {}),
        };
      });
      return (
        <div>
          {tc.text && <p className="text-sm whitespace-pre-wrap break-words">{tc.text}</p>}
          {renderQuickReplyButtons(items)}
        </div>
      );
    }

    return null;
  };

  const getMessageContent = (): string => {
    // Extract text content (caption)
    const textContent = message.content.find(c => c.type === 'text');
    if (textContent && 'text' in textContent) {
      return textContent.text;
    }

    // ✅ Image/video captions are rendered inline inside the media block (see renderMediaPreview)
    // Only document caption remains here as a text fallback.
    const documentContent = message.content.find(c => c.type === 'document');
    if (documentContent && 'caption' in documentContent && documentContent.caption) {
      return String(documentContent.caption);
    }

    // plainText é auto-populado no backend (ex: filename em documentos, address em
    // location) pra busca/última mensagem. Quando há bloco de mídia/interativo, o
    // próprio renderMediaPreview já exibe esse dado — cair no plainText duplica.
    const MEDIA_TYPES = ['image', 'video', 'audio', 'document', 'location', 'contact', 'link', 'album', 'poll', 'poll-vote', 'buttons', 'list', 'template', 'sticker'];
    const hasMediaBlock = message.content.some(c => MEDIA_TYPES.includes(c.type));
    if (!hasMediaBlock && message.plainText && message.plainText.trim()) {
      return message.plainText;
    }

    // ✅ Return empty string when no caption/text exists (mídia sem legenda)
    return '';
  };

  const handleAction = (action: MessageAction) => {
    setShowActions(false);
    setShowDropdownMenu(false);
    setShowEmojiBar(false);
    onAction?.(action, message);
  };

  /**
   * Tenta fazer parse e formatar JSON de forma legível
   * Retorna { isJson: true, formatted: string } se for JSON válido
   * Retorna { isJson: false, formatted: string } se não for JSON
   */
  const tryFormatJson = (content: string): { isJson: boolean; formatted: string } => {
    const trimmed = content.trim();

    // Verificar se parece ser JSON (começa com { ou [)
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return { isJson: false, formatted: content };
    }

    try {
      const parsed = JSON.parse(trimmed);
      const formatted = JSON.stringify(parsed, null, 2);
      return { isJson: true, formatted };
    } catch {
      return { isJson: false, formatted: content };
    }
  };

  // System message with centered layout (P5 Glass — decorative lines flanking)
  if (isSystemMessage) {
    const messageContent = getMessageContent();
    const { isJson, formatted: formattedContent } = tryFormatJson(messageContent);
    const isLongContent = formattedContent.length > 300;

    // Detecta caso especial: mensagem de transferência (gravada pelo backend
    // ao executar `transferConversation`/`autoAssignFromChannel`/`transferTo*`/
    // AI tool). Conteúdo é `SystemContent.action: 'assignment_changed'` e o
    // backend já populou `plainText` com o texto humano em pt-BR via
    // `AssignmentTextEnricher`. Renderizamos com ícone dedicado.
    const firstContent = message.content[0] as { type?: string; action?: string } | undefined;
    const isAssignmentChange =
      firstContent?.type === 'system' && firstContent.action === 'assignment_changed';

    if (isAssignmentChange) {
      const transferText = message.plainText || formattedContent || 'Atribuição alterada';
      return (
        <div
          className="flex justify-center my-2 px-3"
          data-testid="chat-message-assignment-change"
        >
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] max-w-[80%] text-center"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              color: 'var(--theme-muted-text)',
              border: '1px solid var(--theme-card-border)',
            }}
          >
            <Icon name="user-check" className="w-3.5 h-3.5 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
            <span style={{ whiteSpace: 'pre-wrap' }}>{transferText}</span>
          </div>
        </div>
      );
    }

    return (
      <div
        className="flex justify-center my-2 px-3"
        data-testid="chat-message-system"
      >
        <div
          className="px-3 py-2 rounded-xl text-[12px] min-w-0 max-w-[85%] sm:max-w-md"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            color: 'var(--theme-muted-text)',
            border: '1px solid var(--theme-card-border)',
          }}
        >
          {/* Message content with expand/collapse */}
          <div
            className="text-left mb-1 overflow-hidden"
            style={{
              maxHeight: isSystemMessageExpanded || !isLongContent ? 'none' : '120px',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
          >
            {isJson ? (
              <pre
                style={{
                  margin: 0,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '11px',
                  lineHeight: '1.4',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {formattedContent}
              </pre>
            ) : (
              <p style={{ margin: 0 }}>{formattedContent}</p>
            )}
          </div>

          {/* Expand/Collapse button */}
          {isLongContent && (
            <button
              onClick={() => setIsSystemMessageExpanded(!isSystemMessageExpanded)}
              className="flex items-center gap-1 text-xs mt-1 hover:underline"
              style={{ color: 'var(--theme-primary, #6366f1)' }}
            >
              {isSystemMessageExpanded ? (
                <>
                  <Icon name="chevron-up" className="w-3 h-3" />
                  Recolher
                </>
              ) : (
                <>
                  <Icon name="chevron-down" className="w-3 h-3" />
                  Expandir
                </>
              )}
            </button>
          )}

          {/* Timestamp with tool params and AI indicator */}
          <div className="flex items-center gap-1 justify-start mt-1">
            <span style={{ color: 'var(--theme-muted-text)' }}>
              {new Date(message.sentAt).toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            {message.toolCall && (
              <Tooltip
                content={
                  /* Objeto COMPLETO: antes era maxHeight 400 + overflow hidden,
                     que cortava o JSON no meio sem nem mostrar barra de rolagem —
                     com 4 bases no array a bolha parava em "furnished": null e as
                     últimas chaves (reference, searchText, prompt) sumiam, o que
                     atrapalhou o diagnóstico de um caso real em 27/08/2026.
                     A altura agora acompanha o espaço que o Radix informa ter na
                     tela, e o que exceder rola (o conteúdo do tooltip é hoverável). */
                  <div
                    style={{
                      width: 320,
                      maxHeight: 'min(70vh, var(--radix-popper-available-height, 70vh))',
                      overflowY: 'auto',
                    }}
                  >
                    <div className="font-semibold mb-1">Parâmetros da Tool (clique para copiar):</div>
                    <pre
                      className="text-[10px] whitespace-pre-wrap break-words"
                      data-testid="chat-message-tool-params-json"
                    >
                      {JSON.stringify(message.toolCall.arguments, null, 2)}
                    </pre>
                  </div>
                }
              >
                <span className="inline-flex" data-testid="chat-message-tool-params">
                  <Icon name="settings" className="w-3 h-3"
                    style={{ color: 'var(--theme-muted-text)', cursor: 'pointer', display: 'block' }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await navigator.clipboard.writeText(
                          JSON.stringify(message.toolCall?.arguments || {}, null, 2)
                        );
                        // Optional: Show success feedback
                        console.log('✅ Argumentos copiados para área de transferência');
                      } catch (error) {
                        console.error('❌ Erro ao copiar argumentos:', error);
                      }
                    }} />
                </span>
              </Tooltip>
            )}
            {isAIMessage && (
              <Tooltip content="Enviada por inteligência artificial">
                <span className="inline-flex" style={{ pointerEvents: 'auto' }} data-testid="chat-message-ai-indicator">
                  <Icon name="cpu" className="w-3 h-3"
                    style={{ color: 'var(--theme-muted-text)', cursor: 'pointer', display: 'block' }} />
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isForwarded = Boolean(message.forwardedFromMessageId || message.isForwarded);

  // Agrupa reações por emoji (usada tanto no meta inline quanto no popover)
  const buildGroupedReactions = () => {
    const grouped = new Map<string, { count: number; reactors: { name: string; jid: string; userId?: string; emoji: string }[] }>();
    for (const r of message.reactions || []) {
      const existing = grouped.get(r.emoji) || { count: 0, reactors: [] };
      existing.count += 1;
      existing.reactors.push({
        name: r.userName || r.contactName || '',
        jid: r.remoteJid || '',
        userId: r.userId,
        emoji: r.emoji,
      });
      grouped.set(r.emoji, existing);
    }
    return grouped;
  };

  const formatMetaTime = (iso: string): string =>
    new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const renderMeta = () => {
    const metaColor = isOwn ? 'rgba(255, 255, 255, 0.72)' : 'var(--theme-muted-text)';
    // Áudio sem transcrição (e sem texto próprio) ganha a ação "Transcrever" na linha de meta
    const canTranscribe =
      message.content.some((c) => c.type === 'audio') &&
      !message.content.some((c) => c.type === 'text') &&
      !message.plainText?.trim();

    return (
      <div
        ref={reactionDropdownRef}
        className={`${canTranscribe ? 'flex w-full' : 'inline-flex'} items-center gap-1.5 self-end flex-wrap mt-1`}
        style={{ fontSize: 10.5, color: metaColor }}
        data-testid="chat-message-meta"
      >
        {/* AI/Automation icons (inbound only — outbound shows inside MessageStatus) */}
        {!isOwn && isAIMessage && (
          <Tooltip content="Enviada por inteligência artificial">
            <span className="inline-flex" style={{ pointerEvents: 'auto' }} data-testid="chat-message-ai-indicator">
              <Icon name="cpu" className="w-3 h-3 text-indigo-400"
                style={{ cursor: 'pointer', display: 'block' }} />
            </span>
          </Tooltip>
        )}
        {!isOwn && isAutomationMessage && (
          <Tooltip content="Enviado por um workflow">
            <span className="inline-flex" style={{ pointerEvents: 'auto' }} data-testid="chat-message-automation-indicator">
              <Icon name="zap" className="w-3 h-3 text-indigo-400"
                style={{ cursor: 'pointer', display: 'block' }} />
            </span>
          </Tooltip>
        )}

        {/* Transcrever fica no lado oposto ao horário (mr-auto empurra o resto pra direita) */}
        {canTranscribe && (
          <span className="mr-auto inline-flex">
            <TranscribeButton messageId={message.id} />
          </span>
        )}

        {message.isEdited && (
          <span className="italic opacity-70 text-[10px] mr-0.5" data-testid="chat-message-edited">
            editada
          </span>
        )}

        <span className="tabular-nums" data-testid="chat-message-time">
          {formatMetaTime(message.sentAt)}
        </span>

        {isOwn && message.status !== 'failed' && (
          <MessageStatus
            status={message.status as MessageStatusType}
            showTimestamp={false}
            isAI={isAIMessage}
            isAutomation={isAutomationMessage}
          />
        )}
      </div>
    );
  };

  return (
    <>
    {/* Template Modal — renderiza fora do balão para evitar z-index issues */}
    {templateModalOpen && channelId && (
      <SendTemplateModal
        isOpen={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        conversationId={message.conversationId}
        channelId={channelId}
      />
    )}
    <div
      className={`group flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4`}
      data-testid="chat-message"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => {
        // Só esconder triggers se nenhum menu estiver aberto
        if (!showDropdownMenu && !showEmojiBar) {
          setShowActions(false);
        }
      }}
      onClick={(e) => {
        // Mobile: tap no balão abre o action sheet (guarda links/mídia/botões)
        if (!isMobile || !onAction) return;
        const t = e.target as HTMLElement;
        if (t.closest('a, button, img, video, audio, input, select, textarea, [role="button"]')) return;
        setShowMobileSheet(true);
      }}
    >
      {/* Largura do balão: PORCENTAGEM no mobile (acompanha a tela) e teto em pixel
          só a partir de `sm`. Teto fixo de 448px (`max-w-md`) numa tela de 390px era
          o bug 2026-08-19: com um token indivisível (URL longa) o mínimo automático
          do item flex vai até o teto — e o teto ficava FORA da tela, gerando scroll
          horizontal. `break-words` quebra o texto no desenho mas não reduz o
          min-content, então não segurava sozinho. `min-w-0` é a trava extra. */}
      <div className={`flex items-start gap-2 min-w-0 max-w-[85%] sm:max-w-md ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar */}
        {showAvatar && !isOwn && (
          <Avatar className="w-[26px] h-[26px] self-end" isSquircle>
            <AvatarImage
              src={message.sender?.picture}
              alt={message.sender?.name || message.senderName || 'User'}
              isSquircle
            />
            <AvatarFallback isSquircle>
              <span className="text-indigo-600 font-semibold text-xs">
                {(message.sender?.name || message.senderName || 'U').charAt(0).toUpperCase()}
              </span>
            </AvatarFallback>
          </Avatar>
        )}

        {/* Message Bubble */}
        <div className="relative min-w-0 max-w-full">
          <div
            className={`relative px-4 py-2 transition-all ${
              isHighlighted ? 'ring-2 ring-yellow-400' : ''
            }`}
            style={{
              borderRadius: 12,
              borderBottomLeftRadius: isOwn ? 12 : 4,
              borderBottomRightRadius: isOwn ? 4 : 12,
              background: isOwn ? 'var(--theme-button-primary)' : 'var(--theme-card-background)',
              color: isOwn ? '#ffffff' : 'var(--theme-card-text)',
              border: isOwn ? '1px solid transparent' : '1px solid var(--theme-card-border)',
              boxShadow: isOwn
                ? '0 1px 2px rgba(15, 23, 42, 0.12)'
                : '0 1px 2px rgba(15, 23, 42, 0.05)',
              opacity: message.status === 'pending' ? 0.6 : 1,
              ...(isForwarded && {
                borderLeft: `3px solid ${isOwn ? 'rgba(255, 255, 255, 0.8)' : 'var(--theme-button-primary)'}`,
              }),
            }}
          >
          {/* Forwarded indicator — outbound (forwardedFromMessageId) ou inbound (isForwarded) */}
          {isForwarded && (
            <span
              data-testid="chat-message-forwarded-label"
              className="inline-flex items-center gap-1 text-[10.5px] italic font-medium mb-1 opacity-85"
              style={{
                color: isOwn
                  ? 'rgba(255, 255, 255, 0.85)'
                  : 'var(--theme-muted-text)',
              }}
            >
              <Icon name="corner-up-right" className="w-[11px] h-[11px]" />
              Encaminhada
            </span>
          )}

          {/* Sender name (for group chats) */}
          {!isOwn && (message.sender?.name || message.senderName) && (
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--theme-primary)' }}>
              {message.sender?.name || message.senderName}
            </p>
          )}

          {/* Reply Preview - ✅ NOVO: Exibe mensagem respondida */}
          {reply && (
            <MessageReplyPreview
              reply={reply}
              isOwnMessage={isOwn}
            />
          )}

          {/* Media Preview */}
          {renderMediaPreview()}

          {/* Content */}
          {(() => {
            const content = getMessageContent();
            if (!content) return null;
            // Detectar se o conteudo e HTML (email)
            const isHtml = /<[a-z][\s\S]*>/i.test(content);
            if (isHtml) {
              return (
                <div
                  className="text-sm break-words email-content"
                  dangerouslySetInnerHTML={{ __html: content }}
                  style={{ lineHeight: '1.5' }}
                />
              );
            }
            return (
              <p className="text-sm whitespace-pre-wrap break-words">
                {content}
              </p>
            );
          })()}

          {/* Failed Message Display */}
          {isOwn && message.status === 'failed' && message.failedReason && (
            <div
              className="flex items-start gap-1.5 mt-1.5 px-2 py-1.5 rounded text-[11px]"
              style={{
                background: isOwn ? 'rgba(255,255,255,0.15)' : 'rgba(239,68,68,0.08)',
                borderLeft: '3px solid #ef4444',
                color: isOwn ? '#fecaca' : '#ef4444',
              }}
              data-testid="chat-message-failed"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="flex-shrink-0 mt-0.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" x2="12" y1="8" y2="12" />
                <line x1="12" x2="12.01" y1="16" y2="16" />
              </svg>
              <div className="flex-1 min-w-0">
                <strong className="text-[11px] block">Falha no envio</strong>
                <span className="text-[10.5px] opacity-90 break-words">
                  {message.failedReason}
                </span>
              </div>
              {/* B7 (review 2026-07-27): era `||` — fazia o CTA aparecer em
                  Instagram/Messenger também, já que `-1` é a sentinela gravada
                  EXCLUSIVAMENTE pra esses dois canais (nunca WhatsApp). O botão
                  só existe no WhatsApp (spec §2.2) — precisa das DUAS condições. */}
              {(message.failedCode !== undefined && META_WINDOW_CODES.has(message.failedCode) && channelSupportsTemplates) && (
                <button
                  type="button"
                  onClick={() => setTemplateModalOpen(true)}
                  className="ml-1 px-2 py-0.5 text-[10.5px] font-semibold rounded self-start"
                  style={{ background: '#fff', color: '#ef4444', border: '1px solid #ef4444' }}
                  data-testid="chat-message-use-template"
                >
                  Usar Mensagem Template
                </button>
              )}
            </div>
          )}

          {/* Meta row: reactions + edited + time + ticks */}
          {renderMeta()}

          {/* Reactor popover — portal no body */}
          {openReactionEmoji && message.reactions && message.reactions.length > 0 && (() => {
            const grouped = buildGroupedReactions();
            const allReactors = Array.from(grouped.values()).flatMap(g => g.reactors);
            const filteredReactors = reactionTab === 'all' ? allReactors : allReactors.filter(r => r.emoji === reactionTab);

            return createPortal(
              <div
                ref={reactionDropdownRef}
                data-testid="chat-message-reactors"
                style={{
                  position: 'fixed',
                  zIndex: 9999,
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  minWidth: 240,
                  overflow: 'hidden',
                  bottom: reactionDropdownPos.bottom,
                  ...(reactionDropdownPos.left !== undefined ? { left: reactionDropdownPos.left } : {}),
                  ...(reactionDropdownPos.right !== undefined ? { right: reactionDropdownPos.right } : {}),
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {/* Tabs */}
                <div className="flex" style={{ borderBottom: '1px solid #f1f5f9', padding: '0 4px' }}>
                  <button
                    style={{
                      padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer',
                      fontSize: 13, color: reactionTab === 'all' ? '#1e293b' : '#94a3b8',
                      borderBottom: reactionTab === 'all' ? '2px solid #6366f1' : '2px solid transparent',
                      transition: 'all 0.15s',
                    }}
                    onClick={() => setReactionTab('all')}
                  >
                    Todas <span style={{ fontSize: 10, fontWeight: 600, color: reactionTab === 'all' ? '#6366f1' : '#94a3b8' }}>{message.reactions.length}</span>
                  </button>
                  {Array.from(grouped.entries()).map(([emoji, { count }]) => (
                    <button
                      key={emoji}
                      style={{
                        padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer',
                        fontSize: 14, color: reactionTab === emoji ? '#1e293b' : '#94a3b8',
                        borderBottom: reactionTab === emoji ? '2px solid #6366f1' : '2px solid transparent',
                        transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 4,
                      }}
                      onClick={() => setReactionTab(emoji)}
                    >
                      <span style={{ color: 'initial', WebkitTextFillColor: 'initial' }}>{emoji}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: reactionTab === emoji ? '#6366f1' : '#94a3b8' }}>{count}</span>
                    </button>
                  ))}
                </div>

                {/* Lista de quem reagiu */}
                {filteredReactors.map((reactor, i) => {
                  const phone = reactor.jid.replace('@s.whatsapp.net', '');
                  const displayName = reactor.name || phone || 'Desconhecido';
                  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                  const isMe = reactor.userId && reactor.userId === currentUserId;
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-2.5"
                      style={{
                        padding: '10px 14px',
                        borderBottom: i < filteredReactors.length - 1 ? '1px solid #f8fafc' : 'none',
                        background: isMe ? '#faf5ff' : 'transparent',
                      }}
                    >
                      <div
                        className="flex items-center justify-center flex-shrink-0"
                        style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: isMe ? '#ede9fe' : '#e2e8f0',
                          fontSize: 13, fontWeight: 600,
                          color: isMe ? '#7c3aed' : '#64748b',
                        }}
                      >
                        {initials}
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }} className="truncate">
                          {isMe ? 'Você' : displayName}
                        </span>
                        <span style={{ fontSize: 11, color: '#94a3b8', WebkitTextFillColor: 'initial' as never }}>
                          <span style={{ color: 'initial', WebkitTextFillColor: 'initial' }}>{reactor.emoji}</span>
                        </span>
                      </div>
                      {isMe && (
                        <button
                          style={{
                            fontSize: 11, color: '#94a3b8', border: '1px solid #e2e8f0',
                            background: 'transparent', padding: '3px 8px', borderRadius: 4,
                            cursor: 'pointer', transition: 'all 0.15s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#ef4444';
                            e.currentTarget.style.borderColor = '#fecaca';
                            e.currentTarget.style.background = '#fef2f2';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#94a3b8';
                            e.currentTarget.style.borderColor = '#e2e8f0';
                            e.currentTarget.style.background = 'transparent';
                          }}
                          onClick={() => {
                            setOpenReactionEmoji(null);
                            onRemoveReaction?.(message.id, reactor.emoji);
                          }}
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>,
              document.body
            );
          })()}
        </div>

          {/* Reações — chips abaixo do balão (irmãos do balão, não dentro dele) */}
          {message.reactions && message.reactions.length > 0 && (
            <div
              className={`flex flex-wrap gap-1 mt-[5px] ${isOwn ? 'justify-end' : 'justify-start'}`}
              style={{ userSelect: 'none' }}
            >
              {Array.from(buildGroupedReactions().entries()).map(([emoji, { count }]) => (
                <span
                  key={emoji}
                  role="button"
                  tabIndex={0}
                  className="inline-flex items-center gap-1 rounded-full leading-tight cursor-pointer"
                  style={{
                    background: 'var(--theme-card-background)',
                    border: '1px solid var(--theme-card-border)',
                    color: 'var(--theme-card-text)',
                    padding: '1px 7px',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                  onClick={(e) => {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setReactionDropdownPos({
                      bottom: window.innerHeight - rect.top + 6,
                      ...(isOwn ? { right: window.innerWidth - rect.right } : { left: rect.left }),
                    });
                    setReactionTab('all');
                    setOpenReactionEmoji(openReactionEmoji === emoji ? null : emoji);
                  }}
                  data-testid="chat-message-reaction"
                >
                  <span style={{ color: 'initial', WebkitTextFillColor: 'initial' }}>{emoji}</span>
                  {count > 1 && (
                    <span className="text-[9.5px] tabular-nums" style={{ color: 'var(--theme-muted-text)' }}>
                      {count}
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* Ferramentas da mensagem (m-tools) — smile + chevron juntos, no topo, fora do balão.
              Mobile (#087): escondidas — o action sheet é aberto por tap no balão. */}
          {(showActions || showDropdownMenu || showEmojiBar) && onAction && !isMobile && (
            <div
              className={`absolute -top-0.5 flex items-center gap-0.5 ${
                isOwn ? '-left-0.5 -translate-x-full' : '-right-0.5 translate-x-full'
              }`}
            >
              {/* Emoji trigger (m-tbtn) */}
              <button
                ref={emojiBtnRef}
                data-testid="chat-message-emoji-trigger"
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  background: 'var(--theme-card-background)',
                  border: '1px solid var(--theme-card-border)',
                  color: 'var(--theme-muted-text)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'color 0.15s, background 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--theme-card-text)';
                  e.currentTarget.style.background = 'var(--theme-button-ghost-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--theme-muted-text)';
                  e.currentTarget.style.background = 'var(--theme-card-background)';
                }}
                onClick={openEmojiBar}
              >
                <Icon name="smile" style={{ width: 15, height: 15 }} />
              </button>

              {/* Chevron trigger (m-tbtn) */}
              <button
                ref={chevronBtnRef}
                data-testid="chat-message-menu-trigger"
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  background: 'var(--theme-card-background)',
                  border: '1px solid var(--theme-card-border)',
                  color: 'var(--theme-muted-text)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'color 0.15s, background 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--theme-card-text)';
                  e.currentTarget.style.background = 'var(--theme-button-ghost-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--theme-muted-text)';
                  e.currentTarget.style.background = 'var(--theme-card-background)';
                }}
                onClick={openDropdown}
              >
                <Icon name="chevron-down" style={{ width: 15, height: 15 }} />
              </button>
            </div>
          )}

          {/* Dropdown menu — portal no body para evitar z-index da sidebar */}
          {showDropdownMenu && createPortal(
            <div
              ref={dropdownMenuRef}
              data-testid="chat-message-menu"
              style={{
                position: 'fixed',
                top: dropdownPos.top,
                left: dropdownPos.left,
                zIndex: 9999,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                minWidth: 175,
                padding: '4px 0',
                overflow: 'hidden',
              }}
            >
              <button
                className="w-full flex items-center justify-between px-3.5 py-2 hover:bg-gray-50"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: '#374151' }}
                onClick={() => handleAction('reply')}
                data-testid="chat-message-action-reply"
              >
                <span>Responder</span>
                <Icon name="corner-up-left" className="w-4 h-4" style={{ color: '#94a3b8' }} />
              </button>
              <button
                className="w-full flex items-center justify-between px-3.5 py-2 hover:bg-gray-50"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: '#374151' }}
                onClick={() => handleAction('forward')}
                data-testid="chat-message-action-forward"
              >
                <span>Encaminhar</span>
                <Icon name="corner-up-right" className="w-4 h-4" style={{ color: '#94a3b8' }} />
              </button>
              {canEdit && (
                <>
                  <div style={{ height: 1, background: '#f1f5f9', margin: '2px 0' }} />
                  <button
                    className="w-full flex items-center justify-between px-3.5 py-2 hover:bg-gray-50"
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: '#374151' }}
                    onClick={() => handleAction('edit')}
                    data-testid="chat-message-action-edit"
                  >
                    <span>Editar</span>
                    <Icon name="edit" className="w-4 h-4" style={{ color: '#94a3b8' }} />
                  </button>
                </>
              )}
              <div style={{ height: 1, background: '#f1f5f9', margin: '2px 0' }} />
              <button
                className="w-full flex items-center justify-between px-3.5 py-2 hover:bg-red-50"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: '#ef4444' }}
                onClick={() => handleAction('delete')}
                data-testid="chat-message-action-delete"
              >
                <span>Apagar</span>
                <Icon name="trash" className="w-4 h-4" style={{ color: '#ef4444' }} />
              </button>
            </div>,
            document.body
          )}

          {/* Emoji bar — portal no body */}
          {showEmojiBar && createPortal(
            <div
              ref={emojiBarRef}
              className="flex items-center gap-0.5"
              data-testid="chat-message-emoji-picker"
              style={{
                position: 'fixed',
                top: emojiBarPos.top,
                left: emojiBarPos.left,
                zIndex: 9999,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 20,
                padding: 4,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              }}
            >
              {['👍', '❤️', '😂', '😮', '😢', '🙏'].map((emojiChar) => {
                const isActive = myReactedEmojis.has(emojiChar);
                return (
                <button
                  key={emojiChar}
                  className="flex items-center justify-center rounded-full hover:bg-gray-100"
                  style={{
                    width: 34, height: 34, border: 'none',
                    background: isActive ? '#ede9fe' : 'transparent',
                    boxShadow: isActive ? '0 0 0 2px #8b5cf6' : 'none',
                    cursor: 'pointer', fontSize: 20, opacity: 1,
                    color: 'initial', WebkitTextFillColor: 'initial',
                    transition: 'transform 0.1s, background 0.1s',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                  onClick={() => {
                    setShowEmojiBar(false);
                    setShowActions(false);
                    if (isActive) {
                      onRemoveReaction?.(message.id, emojiChar);
                    } else {
                      onReact?.(message.id, emojiChar);
                    }
                  }}
                >
                  {emojiChar}
                </button>
                );
              })}
            </div>,
            document.body
          )}

          {/* Mobile (#087): bottom action sheet (`.asheet`) — reações + ações */}
          {isMobile && showMobileSheet && onAction && createPortal(
            <div className="fixed inset-0 z-[95]" data-testid="chat-message-sheet-root">
              {/* stopPropagation: eventos sintéticos borbulham pela árvore REACT através
                  do portal até o container da mensagem (onClick reabre o sheet) — sem
                  isso o tap no scrim fecha e reabre no mesmo batch (sheet "travado") */}
              <div
                className="absolute inset-0 bg-black/40"
                onClick={(e) => { e.stopPropagation(); setShowMobileSheet(false); }}
              />
              <div
                data-testid="chat-message-sheet"
                className="absolute inset-x-0 bottom-0 rounded-t-[18px] bg-white shadow-xl overflow-hidden"
                style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-center pt-2 pb-1">
                  <span className="w-9 h-1 rounded-full" style={{ backgroundColor: '#e2e8f0' }} />
                </div>
                {/* Fileira de reações (6 emojis) */}
                <div className="flex items-center justify-around px-3 py-2.5 border-b" style={{ borderColor: '#f1f5f9' }}>
                  {['👍', '❤️', '😂', '😮', '😢', '🙏'].map((emojiChar) => {
                    const isActive = myReactedEmojis.has(emojiChar);
                    return (
                      <button
                        key={emojiChar}
                        className="flex items-center justify-center rounded-full"
                        style={{
                          width: 44, height: 44, border: 'none',
                          background: isActive ? '#ede9fe' : 'transparent',
                          boxShadow: isActive ? '0 0 0 2px #8b5cf6' : 'none',
                          fontSize: 26, cursor: 'pointer',
                          color: 'initial', WebkitTextFillColor: 'initial',
                        }}
                        onClick={() => {
                          setShowMobileSheet(false);
                          if (isActive) onRemoveReaction?.(message.id, emojiChar);
                          else onReact?.(message.id, emojiChar);
                        }}
                      >
                        {emojiChar}
                      </button>
                    );
                  })}
                </div>
                {/* Ações da mensagem */}
                <div className="py-1.5">
                  <button
                    className="w-full flex items-center gap-3 px-5 py-3 text-left active:bg-gray-50"
                    style={{ fontSize: 15, color: '#374151' }}
                    onClick={() => { setShowMobileSheet(false); handleAction('reply'); }}
                    data-testid="chat-message-sheet-reply"
                  >
                    <Icon name="corner-up-left" className="w-5 h-5" style={{ color: '#64748b' }} />
                    <span>Responder</span>
                  </button>
                  <button
                    className="w-full flex items-center gap-3 px-5 py-3 text-left active:bg-gray-50"
                    style={{ fontSize: 15, color: '#374151' }}
                    onClick={() => { setShowMobileSheet(false); handleAction('forward'); }}
                    data-testid="chat-message-sheet-forward"
                  >
                    <Icon name="corner-up-right" className="w-5 h-5" style={{ color: '#64748b' }} />
                    <span>Encaminhar</span>
                  </button>
                  {canEdit && (
                    <button
                      className="w-full flex items-center gap-3 px-5 py-3 text-left active:bg-gray-50"
                      style={{ fontSize: 15, color: '#374151' }}
                      onClick={() => { setShowMobileSheet(false); handleAction('edit'); }}
                      data-testid="chat-message-sheet-edit"
                    >
                      <Icon name="edit" className="w-5 h-5" style={{ color: '#64748b' }} />
                      <span>Editar</span>
                    </button>
                  )}
                  <button
                    className="w-full flex items-center gap-3 px-5 py-3 text-left active:bg-red-50"
                    style={{ fontSize: 15, color: '#ef4444' }}
                    onClick={() => { setShowMobileSheet(false); handleAction('delete'); }}
                    data-testid="chat-message-sheet-delete"
                  >
                    <Icon name="trash" className="w-5 h-5" style={{ color: '#ef4444' }} />
                    <span>Apagar</span>
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ChatMessage;
